/*
 * This file is part of SketchBand.
 * Copyright (C) 2016  Mikael Brockman
 * 
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 * 
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 * 
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import React from "react"
import createReactClass from "create-react-class"

import { synthesizeChords } from "./synth.js"
import { scheduleSong } from "./schedule.js"
import { resumeAudio, trackAudioTime, playSchedule } from "./audio.js"
import { parse, songLength } from "./tab.js"
import { Song } from "./sheet.jsx"

const DEFAULT_PLAYBACK_CONTEXT = {
  beatsPerBar: 4,
}

const DEFAULT_TEMPO_BPM = 100
const MIN_TEMPO_BPM = 40
const MAX_TEMPO_BPM = 220
const TEMPO_STEP_BPM = 5
const TEMPO_STORAGE_KEY = "tempo-bpm"

const secondsPerBar = ({ bpm, beatsPerBar }) =>
  (60 / bpm) * beatsPerBar

const clampTempoBpm = bpm =>
  Math.min(MAX_TEMPO_BPM, Math.max(MIN_TEMPO_BPM, bpm))

const loadTempoBpm = () => {
  let storedTempo = parseInt(localStorage.getItem(TEMPO_STORAGE_KEY), 10)
  if (Number.isFinite(storedTempo)) {
    return clampTempoBpm(storedTempo)
  }
  return DEFAULT_TEMPO_BPM
}

function allChordsUsedInSong(song) {
  const object = {}
  song.barSequences.forEach(
    ({ barSequence }) => barSequence.bars.forEach(
      ({ bar }) => bar.voices.forEach(
        voice => {
          if (voice.harmony)
            voice.harmony.chords.forEach(x => object[x.chord.name] = true)
        }
      )
    )
  )
  return Object.keys(object)
}

function needsMoreChords(chords, chordNames) {
  return chordNames.every(x => chords[x])
}

export const SongPlayerToolbar = createReactClass({
  render() {
    return (
      <div className="player-toolbar">
        <div className="player-toolbar-meta">
          <div className="player-toolbar-tempo">
            <button onClick={this.props.tempoDown}>-</button>
            <input
              type="range"
              min={MIN_TEMPO_BPM}
              max={MAX_TEMPO_BPM}
              step={TEMPO_STEP_BPM}
              value={this.props.tempoBpm}
              onChange={this.changeTempo}
              aria-label="Tempo in beats per minute"
            />
            <button onClick={this.props.tempoUp}>+</button>
            <span className="player-toolbar-tempo-value">
              {this.props.tempoBpm} BPM
            </span>
          </div>
        </div>
        <div className="player-toolbar-buttons">
          <button onClick={this.transposeReset}>0</button>
          <button onClick={this.transposeUp}>↑</button>
          <button onClick={this.transposeDown}>↓</button>
          <button onClick={this.props.togglePlayback}>
            {this.props.isPlaying ? "Pause" : "Play"}
          </button>
        </div>
      </div>
    )
  },

  changeTempo(event) {
    this.props.changeTempo(parseInt(event.target.value, 10))
  },

  transposeReset() {
    this.transposeBy(-this.props.transposeSteps)
  },

  transposeUp() {
    this.transposeBy(1)
  },

  transposeDown() {
    this.transposeBy(-1)
  },

  transposeBy(n) {
    window.dispatch("transposition-changed", {
      songId: this.props.song._id,
      semitones: this.props.transposeSteps + n
   })
  },
})

export const SongPlayer = createReactClass({
  getInitialState() {
    return {
      timeInSeconds: 0,
      synthesizedChords: {},
      barProgress: 0,
      isPlaying: false,
      tempoBpm: loadTempoBpm(),
    }
  },

  componentDidMount() {
    this.resynthesize(this.props)
  },

  componentWillUnmount() {
    this.stopPlayback()
  },

  UNSAFE_componentWillReceiveProps(next) {
    if (
      next.song._id !== this.props.song._id ||
      next.song.content !== this.props.song.content ||
      next.transposeSteps !== this.props.transposeSteps
    ) {
      this.stopPlayback()
      this.setState({ barProgress: 0, isPlaying: false })
    }
    this.resynthesize(next)
  },

  resynthesize(props) {
    const chordNames = allChordsUsedInSong(this.parseSong(props))
    if (!needsMoreChords(this.state.synthesizedChords, chordNames)) {
      this.setState({ synthesizedChords: {} })
      synthesizeChords(
        chordNames
      ).then(synthesizedChords => {
        this.setState({ synthesizedChords })
      }).catch(e => {
        console.error(e)
      })
    }
  },

  ensureSynthesizedChords(props = this.props) {
    const chordNames = allChordsUsedInSong(this.parseSong(props))

    if (needsMoreChords(this.state.synthesizedChords, chordNames)) {
      return Promise.resolve(this.state.synthesizedChords)
    }

    return synthesizeChords(chordNames).then(synthesizedChords => {
      this.setState({ synthesizedChords })
      return synthesizedChords
    })
  },

  render() {
    const { song } = this.props
    const { synthesizedChords, barProgress, isPlaying, tempoBpm } = this.state
    const playbackContext = this.playbackContext()
    const barDuration = secondsPerBar(playbackContext)
    
    const parsedSong = this.parseSong()
    const originalChords = allChordsUsedInSong(
      this.parseSong({ ...this.props, transposeSteps: 0 })
    )
    
    return (
      <div className="song-player">
        <div className="player-toolbar-print-info">
          <span className="title">
            {song.title}
          </span>
          {' by '}
          <span className="author">
            {song.author}
          </span>
        </div>
        <div className="song-player-screen-header">
          <div className="song-player-screen-kicker">SketchBand</div>
          <div className="song-player-screen-title">{song.title}</div>
          <div className="song-player-screen-author">{song.author}</div>
        </div>
        <Song
          song={parsedSong}
          t={barProgress}
          onClickBar={this.seekToBar}
          beatsPerBar={playbackContext.beatsPerBar}
          secondsPerBar={barDuration}
        />
        <SongPlayerToolbar
          song={song}
          isPlaying={isPlaying}
          togglePlayback={this.togglePlayback}
          tempoBpm={tempoBpm}
          changeTempo={this.changeTempo}
          tempoDown={this.tempoDown}
          tempoUp={this.tempoUp}
          chords={Object.keys(synthesizedChords)}
          originalChords={originalChords}
          transposeSteps={this.props.transposeSteps}
        />
      </div>
    )
  },

  parseSong(props = this.props) {
    return parse(
      props.song.content,
      props.transposeSteps
    ).song
  },

  playbackContext() {
    return {
      ...DEFAULT_PLAYBACK_CONTEXT,
      bpm: this.state.tempoBpm,
    }
  },

  totalBars(props = this.props) {
    return songLength({ song: this.parseSong(props) })
  },

  maxBarProgress(props = this.props) {
    return Math.max(this.totalBars(props) - 0.001, 0)
  },

  clampBarProgress(barProgress, props = this.props) {
    return Math.min(Math.max(barProgress, 0), this.maxBarProgress(props))
  },

  stopPlayback() {
    this.playbackRequestId = (this.playbackRequestId || 0) + 1

    this.stopActivePlayback()
  },

  stopActivePlayback() {
    if (this.cancelAudioTracking) {
      this.cancelAudioTracking()
      this.cancelAudioTracking = null
    }

    if (this.transport) {
      this.transport.stop()
      this.transport = null
    }
  },

  togglePlayback() {
    if (this.state.isPlaying) {
      this.pause()
    } else {
      this.play()
    }
  },

  changeTempo(nextTempoBpm) {
    if (!Number.isFinite(nextTempoBpm)) return
    this.setTempoBpm(clampTempoBpm(nextTempoBpm))
  },

  tempoDown() {
    this.setTempoBpm(this.state.tempoBpm - TEMPO_STEP_BPM)
  },

  tempoUp() {
    this.setTempoBpm(this.state.tempoBpm + TEMPO_STEP_BPM)
  },

  setTempoBpm(nextTempoBpm) {
    let tempoBpm = clampTempoBpm(nextTempoBpm)
    if (tempoBpm === this.state.tempoBpm) return

    localStorage.setItem(TEMPO_STORAGE_KEY, tempoBpm)

    if (this.state.isPlaying) {
      this.setState({ tempoBpm }, () => this.playFrom(this.state.barProgress))
    } else {
      this.setState({ tempoBpm })
    }
  },

  async play() {
    let startAtBar = this.clampBarProgress(this.state.barProgress)
    if (startAtBar >= this.maxBarProgress() && this.totalBars() > 0) {
      startAtBar = 0
    }
    await this.playFrom(startAtBar)
  },

  async playFrom(startAtBar) {
    let requestId = (this.playbackRequestId || 0) + 1
    this.playbackRequestId = requestId

    const synthesizedChords = await this.ensureSynthesizedChords()
    if (Object.keys(synthesizedChords).length === 0) return
    if (requestId !== this.playbackRequestId) return

    let playbackContext = this.playbackContext()
    let clampedBar = this.clampBarProgress(startAtBar)
    let startAtSeconds = clampedBar * secondsPerBar(playbackContext)
    let stopAtSeconds = this.totalBars() * secondsPerBar(playbackContext)

    await resumeAudio()
    if (requestId !== this.playbackRequestId) return

    this.stopActivePlayback()
    this.setState({
      barProgress: clampedBar,
      isPlaying: true,
    })
    this.cancelAudioTracking = trackAudioTime(
      playbackContext,
      this.updateAudioTime,
      {
        startAt: startAtSeconds,
        stopAt: stopAtSeconds,
      }
    )

    let transport = await playSchedule(
      synthesizedChords,
      scheduleSong(
        playbackContext,
        { song: this.parseSong() }
      ),
      {
        startAt: startAtSeconds,
        stopAt: stopAtSeconds,
      }
    )

    if (requestId !== this.playbackRequestId) {
      transport.stop()
      return
    }

    this.transport = transport
  },

  pause() {
    this.stopPlayback()
    this.setState({ isPlaying: false })
  },

  seekToBar(barNumber) {
    let barProgress = this.clampBarProgress(barNumber)
    if (this.state.isPlaying) {
      this.playFrom(barProgress)
    } else {
      this.setState({ barProgress })
    }
  },

  updateAudioTime(t, barProgress, { done }) {
    this.setState({
      timeInSeconds: t,
      barProgress,
      isPlaying: !done,
    })

    if (done) {
      this.stopPlayback()
    }
  }
})
